import {
  useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useLanguage from 'hooks/useLanguage';
import { Mixpanel } from 'mixpanel';
import useFocusedModule from 'hooks/useFocusedModule';
import useFile from 'api/hooks/useFile';
import useFileBrief from 'api/hooks/useFileBrief';
import { AlkaliProps } from 'utils/types';
import useUser from 'api/hooks/useUser';
import Loading from 'components/Loading';

import FeasibilityCheckContent from './FeasibilityCheckContent';

type OptionType = {
  label: string;
  value: string;
} | null | undefined;

export interface BriefInitialProps {
  watchCompanyName?: string;
  industry: string;
  subIndustry?: string;
  apps: string;
  products: string;
}

interface IApplicationData {
  companyName: string;
  industry: OptionType;
  apps: OptionType;
  product: OptionType;
  subIndustry: OptionType;
  productsOptions: string[];
}

interface IEconomicData {
  alkali: OptionType;
  concentration: OptionType;
  consumption: string;
  price: string;
  annualExpenditure: string | number;
  omya: OptionType;
  concentrationOmya: OptionType;
  costDelivered: string;
  expectedProductConsumption: number;
}

interface IProcessSteps {
  title: string;
  isSptepsCheck: boolean;
  isAlkaliCheck: boolean;
}

interface IPlantSetUpData {
  averageInflow: string;
  processSteps: IProcessSteps[];
}

interface IChemicalData {
  product: string;
  concentration: string;
  expectedProductConsumption: string;
  dosageConcentration: number;
  startPh: string;
  targetPh: string;
  stirredTank: string;
  aeration: OptionType;
  reactionTime: string;
  chemicalFeasibility: string;
  sulfate: OptionType;
}

interface ITechnicalData {
  product: OptionType;
  annualConsumption: string;
  productPackaging: OptionType;
  pumpType: OptionType;
  rentalPump: OptionType;
  linesMore: OptionType;
  linesLess: OptionType;
  valves: OptionType;
  risk: OptionType;
  productBefore: OptionType;
  stirredTank: OptionType;
  productRecirculation: OptionType;
  productWithdrawel: OptionType;
  numberFtl: number;
  timeDeliveries: number;
  tankBottom: OptionType;
  expectedProductConsumption: string;
}

export interface IFeasibilityCheck {
  application: IApplicationData;
  economic: IEconomicData;
  plant: IPlantSetUpData;
  chemical: IChemicalData;
  technical: ITechnicalData;
}

const FeasibilityCheck = () => {
  const { i18n } = useTranslation();
  const { data } = useFile({ type: 'converter' });
  const { data: brief } = useFileBrief();
  const { data: user } = useUser();
  const { blueLang } = useLanguage();
  const [wasLanguageDetected, setWasLanguageDetected] = useState(blueLang.length > 0);

  const { control, setValue, watch } = useForm<IFeasibilityCheck>({
    defaultValues: {
      application: {},
      economic: {},
      plant: {
        averageInflow: '1000',
      },
      chemical: {
        startPh: '2',
        targetPh: '7',
        reactionTime: '15',
        expectedProductConsumption: '1',
      },
      technical: {},
    },
  });

  useFocusedModule('Feasibility Check Module Duration');

  useEffect(() => {
    if (user) {
      Mixpanel.track('Feasibility Check Opened', {
        role: user?.role?.name,
        organization: user?.organization,
      });
    }
  }, [user]);

  useEffect(() => {
    if (blueLang.length > 0) {
      i18n.changeLanguage(blueLang)
        .finally(() => setWasLanguageDetected(true));
    }
  }, [blueLang]);

  const filteredData = useMemo(() => {
    const omyaData: AlkaliProps[] = [];
    const productData: AlkaliProps[] = [];

    data?.forEach((products) => {
      if (products?.['Material A']?.includes('Omya')) {
        omyaData.push(products);
      } else {
        productData.push(products);
      }
    });

    return {
      omyaData,
      productData,
    };
  }, [data]);

  const [activeTab, setActiveTab] = useState<number>(1);

  const isDisabledDownload = useMemo(() => {
    if (activeTab === 1) {
      return false;
    }

    return true;
  }, [activeTab]);

  const tabArr = [
    {
      id: 1,
      title: 'application',
    },
    {
      id: 2,
      title: 'economic',
    },
    {
      id: 3,
      title: 'plantSetUp',
    },
    {
      id: 4,
      title: 'chemical',
    },
    {
      id: 5,
      title: 'technical',
    },
    {
      id: 6,
      title: 'report',
    },
  ];

  const setActiveTabHandler = (id: number) => {
    setActiveTab(id);
  };

  if (!wasLanguageDetected) {
    return (
      <div className="flex mt-[-50px] justify-center items-center h-screen">
        <Loading size={20} color="#000" />
      </div>
    );
  }

  return <FeasibilityCheckContent
    tabArr={tabArr}
    setActiveTabHandler={setActiveTabHandler}
    activeTab={activeTab}
    setActiveTab={setActiveTab}
    isDisabledDownload={isDisabledDownload}
    brief={brief}
    filteredData={filteredData}
    control={control}
    setValue={setValue}
    formWatch={watch}
  />;
};

export default FeasibilityCheck;
